import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import Button from 'src/components/Button';
import Gap from 'src/components/Gap';
import LoginModal from 'src/components/LoginModal';
import ModalLayoutBase, { MobileModalType, ModalCloseButton, ModalContent as ModalContentBase, ModalHeader } from 'src/components/ModalLayout';
import OptimizeImage from 'src/components/OptimizeImage';
import ResizingText from 'src/components/ResizingText';
import RecentMatchCounter from 'src/components/VividiOnly/RecentMatchCounter';
import useMatchCount from 'src/hooks/useMatchCount/rest';
import { hasInduceRegisterBeenShownAtom } from 'src/stores/auth/atoms';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { EVENT_LOGIN_MODAL_VISITOR_TRIGGER, EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { hexToRgb } from 'src/utils/common';
import { EventData, pushEvent } from 'src/utils/event-gateway';
const ModalLayout = styled(ModalLayoutBase)`
  max-width: 400px;
  background-color: ${({
  theme
}) => theme.color.bg2};

  ${({
  theme
}) => theme.screenSize.mobile} {
    max-width: unset;
  }
`;
const ModalContent = styled(ModalContentBase)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 209px 40px 32px 40px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    align-items: center;
    padding-top: 0px;
  }
`;
const CloseButton = styled(ModalCloseButton)`
  display: flex;
  ${({
  theme
}) => theme.isRtl ? 'left' : 'right'}: 16px;
  ${({
  theme
}) => theme.isRtl ? 'right' : 'left'}: auto;
`;
const ModalTitle = styled.p`
  color: ${({
  theme
}) => theme.color.contentDeep};
  font-size: 30px;
  font-weight: 700;
  line-height: 40px;
  margin-bottom: 12px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    text-align: center;
  }
`;
const ModalDescription = styled.p`
  color: ${({
  theme
}) => theme.color.content11};
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 24px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    display: none;
  }
`;
const MatchCounterWrap = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    justify-content: center;
    margin-bottom: 20px;
  }
`;
const MatchCounterContainer = styled(ResizingText)`
  display: flex !important;
  font-size: 15px;
  line-height: 20px;
  font-weight: 400;
  color: ${({
  theme
}) => theme.color.contentDeep};

  ${({
  theme
}) => theme.screenSize.breakpointUp(360)} {
    justify-content: center;
    font-size: 15px !important;
  }
`;
const MatchCounter = styled(RecentMatchCounter)``;
const CounterDesc = styled.p`
  word-break: break-word;
`;
const CounterDot = styled.div`
  width: 8px;
  height: 8px;
  background-color: ${({
  theme
}) => theme.color.accentH1};
  border-radius: 4px;
`;
const DesktopImageWrap = styled.div`
  position: absolute;
  ${({
  theme
}) => theme.isRtl ? 'left' : 'right'}: 59px;
  transform: ${({
  theme
}) => theme.isRtl ? 'rotate(8deg)' : 'rotate(-8deg)'};
  transform-origin: ${({
  theme
}) => theme.isRtl ? 'right' : 'left'};

  ${({
  theme
}) => theme.screenSize.mobile} {
    display: none;
  }
`;
const MobileImageWrap = styled.div`
  position: relative;
  flex: 1;
  justify-content: center;
  flex-direction: column;
  margin-bottom: 16px;

  ::after {
    content: '';
    position: absolute;
    inset-block-end: 0;
    inset-inline: 0;
    height: 60px;
    ${({
  theme
}) => `background: linear-gradient(to bottom, rgba(${hexToRgb(theme.color.bg2)}, 0), ${theme.color.bg2});`}
  }

  display: none;

  ${({
  theme
}) => theme.screenSize.mobile} {
    display: flex;
  }
`;
const MobileImage = styled(OptimizeImage)`
  min-height: 0;
  // 80vh 내에서 이미지가 레이아웃을 해치지 않으면서 최대로 늘어날 수 있는 높이
  width: 56dvw;
  height: 39dvh;
  object-fit: cover;
  object-position: center top;
`;
const ContentWrap = styled.div`
  display: flex;
  flex-direction: column;

  ${({
  theme
}) => theme.screenSize.mobile} {
    flex-direction: column-reverse;
  }
`;
const ChatButton = styled(Button)`
  flex-shrink: 0;
`;
interface Props {
  eventParams: EventData['eventParams'];
}
const VividiInduceRegisterModal = ({
  eventParams
}: Props) => {
  const {
    t
  } = useTranslation();
  const closeModal = useSetAtom(closeModalAtom);
  const showModal = useSetAtom(showModalAtom);
  const [isBelowThreshold, setIsBelowThreshold] = useState(true);
  const setHasInduceRegisterBeenShown = useSetAtom(hasInduceRegisterBeenShownAtom);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.POPUP,
      eventName: EVENT_NAME.ANYWHERE_IMPRESSION_BOUNCE_POPUP,
      eventParams: {
        ...eventParams,
        action_category: 'impression',
        tab: 'anywhere',
        page: 'anywhere',
        target: 'bounce_popup'
      }
    });
  }, [eventParams]);
  useEffect(() => {
    setHasInduceRegisterBeenShown(true);
  }, [setHasInduceRegisterBeenShown]);
  const handleClose = () => {
    closeModal(ModalType.INDUCE_REGISTER_MODAL);
  };
  const matchCount = useMatchCount();
  useEffect(() => {
    const threshold = 10_000;
    setIsBelowThreshold(matchCount < threshold);
  }, [matchCount]);
  const handleShowLoginModal = () => {
    handleClose();
    pushEvent({
      eventType: EVENT_TYPE.BUTTON,
      eventName: EVENT_NAME.ANYWHERE_CLICK_BOUNCE_POPUP_BUTTON,
      eventParams: {
        ...eventParams,
        action_category: 'click',
        tab: 'anywhere',
        page: 'anywhere',
        target: 'bounce_popup_button'
      }
    });
    showModal({
      key: ModalType.LOGIN,
      component: () => <LoginModal trigger={EVENT_LOGIN_MODAL_VISITOR_TRIGGER.BOUNCE_POPUP} />
    });
  };
  return <ModalLayout isDim closeWhenClickOutside={false} mobileType={MobileModalType.BOTTOM_SHEET} navButtonType={ModalNavButtonType.NONE} // 별도 디자인을 위해 직접 닫기 버튼 추가
  mobileNavButtonType={ModalNavButtonType.NONE} onClose={handleClose} data-sentry-element="ModalLayout" data-sentry-component="VividiInduceRegisterModal" data-sentry-source-file="index.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx">
        <CloseButton onClick={handleClose} data-sentry-element="CloseButton" data-sentry-source-file="index.tsx" />
      </ModalHeader>
      <DesktopImageWrap data-sentry-element="DesktopImageWrap" data-sentry-source-file="index.tsx">
        <OptimizeImage width={400} height={255} alt='Start Free 1:1 Video Chat' src={`/vividi/images/modal/bounce-rate/bounce-rate-desktop.png`} sizes='(max-width: 1280px) 400px, 100vw' data-sentry-element="OptimizeImage" data-sentry-source-file="index.tsx" />
      </DesktopImageWrap>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <ContentWrap data-sentry-element="ContentWrap" data-sentry-source-file="index.tsx">
          <MatchCounterWrap data-sentry-element="MatchCounterWrap" data-sentry-source-file="index.tsx">
            <CounterDot data-sentry-element="CounterDot" data-sentry-source-file="index.tsx" />
            <Gap width={6} data-sentry-element="Gap" data-sentry-source-file="index.tsx" />
            <MatchCounterContainer minFontSize={10} maxLines={1} data-sentry-element="MatchCounterContainer" data-sentry-source-file="index.tsx">
              {isBelowThreshold ? <span>
                  {[t('vividi_mirror_matchcounter_lowactivity_alt_title_text'), t('vividi_mirror_matchcounter_lowactivity_alt_sub_text')].join(' ')}
                </span> : <>
                  <MatchCounter />
                  <Gap width={4} />
                  <CounterDesc>{t('online__matching_now')}</CounterDesc>
                </>}
            </MatchCounterContainer>
          </MatchCounterWrap>
          <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="index.tsx">{t('popup_join_now_title')}</ModalTitle>
        </ContentWrap>
        <ModalDescription data-sentry-element="ModalDescription" data-sentry-source-file="index.tsx">{t('popup_join_now_description')}</ModalDescription>
        <MobileImageWrap data-sentry-element="MobileImageWrap" data-sentry-source-file="index.tsx">
          <MobileImage width={102} height={193} src={`/vividi/images/modal/bounce-rate/bounce-rate-mobile.png`} alt='Start Free 1:1 Video Chat' sizes='(max-width: 480px) 157px, 100vw' data-sentry-element="MobileImage" data-sentry-source-file="index.tsx" />
        </MobileImageWrap>
        <ChatButton variant='contained' onClick={handleShowLoginModal} id='bounce-rate-modal-join-now' data-sentry-element="ChatButton" data-sentry-source-file="index.tsx">
          {t('popup_join_now_cta')}
        </ChatButton>
      </ModalContent>
    </ModalLayout>;
};
export default VividiInduceRegisterModal;